import { batch } from '@tanstack/store';
import PropTypes from 'prop-types';
import { use, useCallback, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import slug from 'slug';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import CancelButton from '../../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../../components/Button/predefined/SaveButton/SaveButton';

import ContentTypeFormContext from '../../../contexts/ContentTypeFormContext';
import ListPropertiesContext from '../../../contexts/ListPropertiesContext';
import {
  ModalInstanceContext,
  useModals,
} from '../../../contexts/ModalContext';

import { ArrowLeftThinIcon } from '../../../images/shapes';

import { useAppForm } from '../../../form-hooks/form';
import ListProperties from '../../ContentTypeForm/ListProperties/ListProperties';
import PropertiesSettings from '../PropertiesSettings/PropertiesSettings';
import PropertyCard from '../PropertyCard/PropertyCard';
import { parseInputType } from '../helpers/parseProperty';
import { EDITABLE } from '../helpers/propertiesFields';

export const TRANSLATIONS_KEYS = {
  text: { key: 'Text' },
  textarea: { key: 'Textarea' },
  textMarkdown: { key: 'Markdown' },
  email: { key: 'Email' },
  richtext: { key: 'Richtext' },
  number: { key: 'Number' },
  radio: { key: 'Radio' },
  checkbox: { key: 'Checkbox' },
  select: { key: 'Select' },
  datasource: { key: 'Relation' },
  object: { key: 'List', tooltipKey: 'List' },
  simpleList: { key: 'SimpleList' },
  geo: { key: 'Geo' },
  media: { key: 'Media' },
  dateTime: { key: 'DateTime' },
  block: { key: 'Block' },
};

const PropertyModalFormContent = ({
  onSubmit,
  property,
  validationSchema,
  testId = '',
  parentInputType,
}) => {
  const formId = useId();
  const { t } = useTranslation();
  const modal = useModals();
  const modalInstance = use(ModalInstanceContext);
  const { setDirty, propertiesErrors } = use(ContentTypeFormContext);
  const { isNestedList } = use(ListPropertiesContext);

  const propertyErrors = useMemo(
    () => propertiesErrors[property?.key],
    [propertiesErrors, property?.key],
  );

  const onCancel = useCallback(
    () => modalInstance.resolve(null),
    [modalInstance],
  );

  const labelFieldListeners = useMemo(
    () => ({
      onBlur: ({ value, fieldApi }) => {
        if (fieldApi.form.getFieldValue('key')) return;
        fieldApi.form.setFieldValue('key', slug(value, '_'));
        fieldApi.form.validateField('key');
      },
    }),
    [],
  );

  const initialValues = useMemo(
    () =>
      property
        ? JSON.parse(JSON.stringify(property))
        : {
            key: '',
            config: { label: '', helpText: '', unique: false },
            schema: { type: '' },
            required: false,
          },
    [property],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: onSubmit,
    validators: { onChange: validationSchema },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const handleTypeChange = useCallback(async () => {
    if (property) {
      const result = await modal.confirmation(
        t('PropertyForm.ChangeTypeConfirm'),
      );
      if (!result) return;
    }
    form.setFieldValue('schema.type', '');
    form.setFieldValue('config.inputType', '');
    setDirty(true);
  }, [property, form, setDirty, modal, t]);

  const onPropertyCardClick = useCallback(
    async (inputType) => {
      if (EDITABLE.indexOf(inputType) < 0) {
        alert('Not implemented');
        return;
      }

      const newProperty = parseInputType(inputType, form.state.values, form);

      batch(() => {
        Object.keys(newProperty).forEach((key) => {
          form.setFieldValue(key, newProperty[key]);
        });
      });

      setDirty(true);
      form.validateAllFields();
    },
    [form, setDirty],
  );

  const renderPropertySettings = useCallback(
    (inputType, values) => {
      const isMedia =
        values.config?.validation?.relationContenttype === '_media';
      return (
        <>
          <div>
            <div className="text-sm text-slate-400 mb-1">
              {t('PropertyForm.PropertyType')}
            </div>
            <PropertyCard
              inputType={isMedia ? 'media' : inputType}
              propertyErrors={propertyErrors}
              testId={testId}
            />
            <Button
              onClick={handleTypeChange}
              iconImage={
                <ArrowLeftThinIcon className="w-2.5 text-gray-700 dark:text-blue stroke-2" />
              }
              buttonColor="borderless"
              noPaddings
              buttonSize="xs"
              iconPosition="start"
              additionalClasses="text-sm mt-4"
              {...getTestProps(testId, 'change-type', 'testId')}
            >
              {t('PropertyForm.ChangePropertyType')}
            </Button>

            {inputType === 'object' && (
              <ListProperties
                form={form}
                listName={values.key}
                listPropertiesItems={values.config.items}
                listSchemaItems={values.schema.items}
                parentInputType={
                  parentInputType
                    ? `${parentInputType}_${inputType}`
                    : inputType
                }
                isOpen={true}
                additionalClasses="px-0"
                {...getTestProps(testId, `${values.key}-properties`, 'testId')}
              />
            )}
          </div>

          <PropertiesSettings
            form={form}
            inputType={inputType}
            isMedia={isMedia}
            testId={testId}
          />
        </>
      );
    },
    [t, testId, handleTypeChange, form, parentInputType, propertyErrors],
  );

  useEffect(() => {
    const propertyFormErrors = propertyErrors?.form || {};
    if (Object.keys(propertyFormErrors).length > 0) {
      Object.entries(propertyFormErrors).forEach(([key, error]) => {
        form.setFieldMeta(key, (prev) => ({
          ...prev,
          errorMap: { onSubmit: error },
          isTouched: true,
        }));
      });
    }
  }, [form, propertyErrors]);

  const schemaTypeFieldValidators = useMemo(
    () => ({
      onChange: ({ value }) => {
        if (!value) return t('PropertyForm.Errors.Type');
      },
    }),
    [t],
  );

  return (
    <form.AppForm>
      <form
        id={formId}
        className="space-y-2 md:space-y-4 pb-5"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="flex flex-col sm:flex-row gap-2 md:gap-10">
          <form.AppField name="config.label" listeners={labelFieldListeners}>
            {(field) => (
              <field.Input
                label={t('PropertyForm.Label')}
                required
                {...getTestProps(testId, 'label', 'testId')}
              />
            )}
          </form.AppField>

          <form.AppField name="key">
            {(field) => (
              <field.Input
                label={t('PropertyForm.Key')}
                required
                {...getTestProps(testId, 'key', 'testId')}
              />
            )}
          </form.AppField>
        </div>

        {/**
         * There are problems that main validation has no actual data of this field,
         * due to the fact that it is set programmatically.
         */}
        <form.AppField
          name="schema.type"
          validators={schemaTypeFieldValidators}
        >
          {(subField) => (
            <subField.ErrorField
              {...getTestProps(testId, 'schema-type-error', 'testId')}
            />
          )}
        </form.AppField>

        <form.Subscribe selector={({ values }) => values}>
          {(values) => {
            const inputType = values.config?.inputType;
            return inputType ? (
              renderPropertySettings(inputType, values)
            ) : (
              <div
                className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 pb-5"
                {...getTestProps(testId, 'property-cards')}
              >
                {Object.keys(TRANSLATIONS_KEYS).map((inputType) => {
                  if (isNestedList && inputType === 'object') return null;
                  return (
                    <PropertyCard
                      key={inputType}
                      inputType={inputType}
                      onClick={onPropertyCardClick}
                      propertyErrors={propertyErrors}
                      testId={testId}
                    />
                  );
                })}
              </div>
            );
          }}
        </form.Subscribe>

        <div
          className="w-full fixed left-0 bottom-0 flex items-center justify-center p-3 space-x-5
                    border-t border-gray dark:border-slate-800 bg-white dark:bg-gray-900"
        >
          <CancelButton
            onClick={onCancel}
            {...getTestProps(testId, 'cancel', 'testId')}
          />
          <SaveButton
            form={formId}
            {...getTestProps(testId, 'save', 'testId')}
          />
        </div>
      </form>

      <form.ValidationToastHandler hideToast />
    </form.AppForm>
  );
};

export default PropertyModalFormContent;

PropertyModalFormContent.propTypes = {
  /**
   * Property object to edit
   */
  property: PropTypes.object,
  /**
   * Test id for property modal form
   */
  testId: PropTypes.string,
};
